import { CommonModule } from '@angular/common';
import { Component, effect, OnInit, signal } from '@angular/core';
import { AiSolutionUsage, AiSolutionUsageInfo } from '@/app/interfaces/ai-solution-usage';
import {
  AiSolutionUsageAdvancedFilter,
  AiSolutionUsageDateRangeFilterValue,
  AiSolutionUsageListParams,
  AiSolutionUsageSolutionType,
} from '@/app/interfaces/ai-solution-usage-list';
import { filtersAdvanced } from '@/app/interfaces/filtersAdvanced';
import { filtersBasic } from '@/app/interfaces/filtersBasic';
import { NormalizeDatePipe } from '@/app/pipe/normalizeDate.pipe';
import { AiSolutionUsageService } from '@/app/services/ia-services/ai-solution-usage.service';
import { ButtonsComponent } from '@/app/shared/buttons/buttons.component';
import { AiSolutionUsageCardComponent } from '@/app/shared/cards/ai-solution-usage-card/ai-solution-usage-card.component';
import { DropdownComponent } from '@/app/shared/dropdown/dropdown.component';
import { FiltersComponent } from '@/app/shared/filters/filters.component';
import { PaginationComponent } from '@/app/shared/pagination/pagination.component';
import { SearchBarComponent } from '@/app/shared/search-bar/search-bar.component';
import { environment } from '@/environments/environment';

interface AiSolutionUsageListUserData {
  orgCode?: string;
  roleCode?: string;
  roles?: Record<string, string[]>;
}

@Component({
  selector: 'app-ai-solution-usage-list',
  standalone: true,
  imports: [
    CommonModule,
    ButtonsComponent,
    SearchBarComponent,
    FiltersComponent,
    DropdownComponent,
    PaginationComponent,
    AiSolutionUsageCardComponent,
  ],
  templateUrl: './ai-solution-usage-list.component.html',
  styleUrl: './ai-solution-usage-list.component.scss',
  providers: [NormalizeDatePipe],
})
export class AiSolutionUsageListComponent implements OnInit {
  readonly noCreateAiSolutionUsagePermissionMessage = 'No tienes permisos para crear usos de solución IA';
  paginationSize = signal(10);
  currentPage = signal(1);
  cards = signal<AiSolutionUsage[]>([]);
  total = signal(0);
  search = signal('');
  orderByName = signal('');
  orderDirection = signal('desc');

  readonly baseFilters: filtersAdvanced[] = [
    {
      type: 'multiselect',
      label: 'Estado',
      options: [
        { label: 'Aprobado', value: 'APPROVED' },
        { label: 'Pendiente de evaluacion', value: 'PENDING' },
        { label: 'En evaluación', value: 'EVALUATING' },
        { label: 'Rechazado', value: 'REJECTED' },
      ],
      selected: [],
    },
    {
      type: 'dateRange',
      label: 'Fecha de creación',
      dateRangeOptions: {
        startDate: '',
        endDate: '',
      },
      selected: null,
    },
    {
      type: 'multiselect',
      label: 'Tipo',
      options: [
        { label: 'Herramienta', value: 'herramienta' },
        { label: 'Agente', value: 'agente' },
      ],
      selected: [],
    },
  ];
  advancedFilters = signal<filtersAdvanced[]>([...this.baseFilters]);

  state = signal<string[]>([]);
  creationDate = signal<AiSolutionUsageDateRangeFilterValue>({});
  solutionType = signal<AiSolutionUsageSolutionType | undefined>(undefined);

  readonly orderby: filtersBasic = {
    type: 'dropdown',
    label: 'Ordenar usos de solución IA',
    classes: 'text-sm py-1',
    icon: 'pi pi-sort-alt',
    options: [
      { label: 'Ordenar por más reciente', value: 'recent' },
      { label: 'Ordenar por menos reciente', value: 'oldest' },
      { label: 'Ordenar por última modificación', value: 'update' },
    ],
    selected: '',
  };

  constructor(
    private aiSolutionUsageService: AiSolutionUsageService,
    private normalizeDate: NormalizeDatePipe
  ) {
    effect(() => {
      void this.loadAiSolutionUsage();
    });
  }

  ngOnInit(): void {
    localStorage.removeItem('selectedFilters');
  }

  get canReviewAiSolutionUsage(): boolean {
    return !!this.getStoredUserData().roles?.jefe_servicio_OGIA?.length;
  }

  get canCompleteAiSolutionUsage(): boolean {
    return !!this.getStoredUserData().roles?.jefe_consumidor?.length;
  }

  get canCreateAiSolutionUsage(): boolean {
    const userData = this.getStoredUserData();
    const currentOrgCode = userData.orgCode;
    const creatorOrgs = userData.roles?.jefe_consumidor;

    if (!currentOrgCode || !Array.isArray(creatorOrgs)) {
      return false;
    }

    const currentOrgCandidates = [currentOrgCode, `${environment.environment}${currentOrgCode}`];

    return creatorOrgs.some((orgCode) => currentOrgCandidates.includes(orgCode));
  }

  get params(): AiSolutionUsageListParams {
    const advancedFilter: AiSolutionUsageAdvancedFilter = {
      state: this.state(),
      creationDate: this.creationDate(),
      solutionType: this.solutionType(),
      search: this.search(),
    };
    const jobcode = this.getUserJobCode();

    if (
      this.getStoredUserData().roles?.jefe_consumidor?.length &&
      !this.hasServiceManagerGovernanceRole() &&
      jobcode
    ) {
      advancedFilter.jobcode = jobcode;
    }

    return [
      {
        basicFilter: {
          currentPage: this.currentPage(),
          sizePages: this.paginationSize(),
          nameOrderBy: this.orderByName(),
          orderBy: this.orderDirection(),
        },
      },
      { advancedFilter: this.removeEmptyAdvancedFilters(advancedFilter) },
    ];
  }

  handleSearch(value: string): void {
    this.currentPage.set(1);
    this.search.set(value);
  }

  handleApplyFilters(filters: filtersAdvanced[]): void {
    this.currentPage.set(1);

    filters.forEach((filter) => {
      switch (filter.label) {
        case 'Estado':
          this.state.set(this.getSelectedStringValues(filter.selected));
          break;
        case 'Tipo':
          this.solutionType.set(this.getSelectedSolutionType(filter.selected));
          break;
        case 'Fecha de creación':
          if (filter.type === 'dateRange' && filter.dateRangeOptions) {
            this.creationDate.set({
              startDate: this.normalizeDate.transform(filter.dateRangeOptions.startDate),
              endDate: this.normalizeDate.transform(filter.dateRangeOptions.endDate),
            });
          }
          break;
        default:
          break;
      }
    });

    localStorage.setItem('selectedFilters', JSON.stringify(filters));
  }

  onDropdownChange(value: string): void {
    this.currentPage.set(1);

    if (value === 'oldest') {
      this.orderByName.set('');
      this.orderDirection.set('asc');
      return;
    }

    this.orderByName.set(value === 'update' ? 'update' : '');
    this.orderDirection.set('desc');
  }

  private async loadAiSolutionUsage(): Promise<void> {
    const response = await this.aiSolutionUsageService.getAiSolutionUsageList(this.params);
    this.cards.set(
      response.items.map((card) => ({
        ...card,
        infoCard: this.buildInfoCard(card),
      }))
    );
    this.total.set(response.total);
  }

  private getSelectedStringValues(selected?: string | string[] | number | number[]): string[] {
    return Array.isArray(selected) ? selected.filter((value): value is string => typeof value === 'string') : [];
  }

  private getSelectedSolutionType(
    selected?: string | string[] | number | number[]
  ): AiSolutionUsageSolutionType | undefined {
    const values = typeof selected === 'string' ? [selected] : this.getSelectedStringValues(selected);

    if (values.length !== 1) return undefined;

    return values[0] === 'herramienta' || values[0] === 'agente' ? values[0] : undefined;
  }

  private removeEmptyAdvancedFilters(advancedFilter: AiSolutionUsageAdvancedFilter): AiSolutionUsageAdvancedFilter {
    return Object.entries(advancedFilter)
      .filter(([, value]) => {
        if (Array.isArray(value)) return value.length > 0;
        if (typeof value === 'string') return value.trim() !== '';
        if (this.isDateRangeFilterValue(value)) return !!value.startDate || !!value.endDate;
        if (typeof value === 'object' && value !== null) return Object.keys(value).length > 0;
        return value !== undefined && value !== null;
      })
      .reduce<AiSolutionUsageAdvancedFilter>((acc, [key, value]) => ({ ...acc, [key]: value }), {});
  }

  private isDateRangeFilterValue(value: unknown): value is AiSolutionUsageDateRangeFilterValue {
    return typeof value === 'object' && value !== null && ('startDate' in value || 'endDate' in value);
  }

  private getUserJobCode(): string | undefined {
    const jobcode = this.getStoredUserData().roleCode?.trim();

    return jobcode ? jobcode : undefined;
  }

  private hasServiceManagerGovernanceRole(): boolean {
    return !!this.getStoredUserData().roles?.jefe_servicio_OGIA?.length;
  }

  private getStoredUserData(): AiSolutionUsageListUserData {
    const storedUserData = localStorage.getItem('userData');

    if (!storedUserData) {
      return {};
    }

    try {
      return JSON.parse(storedUserData) as AiSolutionUsageListUserData;
    } catch {
      return {};
    }
  }

  private buildInfoCard(card: AiSolutionUsage): AiSolutionUsageInfo[] {
    return [
      {
        label: 'Tipo de solución IA',
        value: card.solutionType,
      },

      {
        label: 'Nombre de la solución IA',
        value: card.solutionName,
      },

      {
        label: 'Correo de contacto',
        value: card.requestingPersonEmail,
      },
      {
        label: 'Departamento solicitante',
        value: card.department,
      },
      {
        label: 'Unidad Organizativa Solicitante',
        value: card.organization,
      },
      // {
      //   label: 'Uso previsto',
      //   value: card.expectedUse,
      // },
    ];
  }
}
